import React, { useState } from "react";
import {
  useDeleteCustomFunctionFiles,
  useGetNewFileDataQuery,
  useUploadNewTestFileQuery,
} from "../../useQuery";
import { useProductQuery } from "../../useQuery";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  Button,
  Stack,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Checkbox,
  Snackbar,
  Alert,
  Paper,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedProduct } from "../../Redux/TestAuthoring/TestAuthoring";
import { CloudUpload as CloudUploadIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { CommonMethods } from "../../components/common/CommonMethods";

const NewDataTab = () => {
  const [uploadNewCustom, setUploadNewCustom] = useState({
    file: "",
    filename: "",
  });

  const [openAlertForZip, setOpenAlertForZip] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [successAlert, setSuccessAlert] = useState({ open: false, message: "" });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: products } = useProductQuery();
  const selectedProduct = useSelector((state) => state.authoringSlice.selectedProduct);

  const {
    data: newFilesData = [],
    isLoading: isLoadingNewFiles,
    isError: isErrorNewFiles,
    refetch: refetchNewFiles,
  } = useGetNewFileDataQuery(selectedProduct, {
    enabled: !!selectedProduct,
    onError: (error) => {
      console.error("Error fetching file data:", error);
      if (error.response?.status === 401) {
        CommonMethods(navigate);
      }
    },
  });

  const { mutate: uploadNewTestFile, isLoading: isTestFileUploading } = useUploadNewTestFileQuery({
    onSuccess: () => {
      setUploadNewCustom({ file: "", filename: "" });
      setSuccessAlert({ open: true, message: "File uploaded successfully" });
      refetchNewFiles();
    },
    onError: (error) => {
      console.error("Error uploading file:", error);
      setSuccessAlert({ open: true, message: "Error uploading file" });
    },
  });

  const { mutate: deleteFiles, isLoading: isDeleting } = useDeleteCustomFunctionFiles({
    onSuccess: () => {
      setSelectedData([]);
      setSelectedItems([]);
      setSuccessAlert({ open: true, message: "Files deleted successfully" });
      refetchNewFiles();
    },
    onError: (error) => {
      console.error("Error deleting files:", error);
      setSuccessAlert({ open: true, message: "Error deleting files" });
    },
  });

  // Table column definitions
  const tableColumnsFromData = [
    { id: 1, name: "Select", align: "center", colspan: 1, width: "15%" },
    { id: 2, name: "File Name", align: "center", colspan: 1, width: "30%" },
    { id: 3, name: "Last Modified", align: "center", colspan: 2, width: "30%" },
    { id: 4, name: "File Size", align: "center", colspan: 1, width: "20%" },
  ];

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    fontSize: 14,
    border: "1px solid #E0E0E0",
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  }));

  const ColorButton = styled(Button)(({ theme }) => ({
    backgroundColor: "#FF5336",
    color: "#FFFFFF",
    "&:hover": {
      backgroundColor: "#E04A31",
    },
  }));

  const UploadBox = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    borderRadius: "8px",
    border: `1px dashed ${theme.palette.mode === "dark" ? "#555" : "#aaa"}`,
    background: "#f9f9f9",
    minHeight: "60px",
    maxWidth: "600px",
  }));

  const FileInfoBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    flexGrow: 1,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  }));

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: "300px",
      },
    },
  };

  const StyledFormControl = styled(FormControl)`
    min-width: 200px;
  `;

  const StyledSelect = styled(Select)`
    border-radius: 5px;
    border: 1px solid #d1dbff;
    font-size: 16px;
    color: #2b3539;
    min-width: 200px;
    max-height: 300px;
    background-color: #ffffff;
    .MuiSelect-select {
      padding: 13px 15px;
    }
  `;

  const handleProductChange = (event) => {
    Promise.resolve().then(() => {
      dispatch(setSelectedProduct(event.target.value));
    });
  };

  const handleFileUpload = () => {
    document.getElementById("upload-custom-file").click();
  };

  const handleUploadNewCustom = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileName = file.name;
      const extension = fileName.split(".").pop().toLowerCase();
      if (extension === "zip") {
        setUploadNewCustom({
          file: e.target.files,
          filename: fileName,
        });
      } else {
        setOpenAlertForZip(true);
        e.target.value = null;
      }
    }
  };

  const handleUploadCustomFile = () => {
    if (uploadNewCustom.file[0]) {
      uploadNewTestFile({
        file: uploadNewCustom.file[0],
        fileName: uploadNewCustom.filename,
        productId: selectedProduct,
      });
    }
  };

  const handleClearFile = () => {
    setUploadNewCustom({ file: "", filename: "" });
  };

  const handleCloseAlert = () => {
    setOpenAlertForZip(false);
  };

  const handleCloseSuccessAlert = () => {
    setSuccessAlert({ ...successAlert, open: false });
  };

  const handleCheckboxToggle = (event, item) => {
    const checked = event.target.checked;
    if (checked) {
      setSelectedData((prev) => [...prev, item.FileName]);
      setSelectedItems((prev) => [...prev, item]);
    } else {
      setSelectedData((prev) => prev.filter((name) => name !== item.FileName));
      setSelectedItems((prev) => prev.filter((i) => i.FileName !== item.FileName));
    }
  };

  const deleteProductFileData = () => {
    if (selectedItems.length > 0) {
      deleteFiles({ files: selectedItems, productId: selectedProduct });
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          {/* Application dropdown */}
          <StyledFormControl>
            <InputLabel id="application-select-label">Application</InputLabel>
            <StyledSelect
              labelId="application-select-label"
              id="application-select"
              MenuProps={MenuProps}
              value={selectedProduct || ""}
              onChange={handleProductChange}
              label="Application"
            >
              {products &&
                products.length > 0 &&
                (products ?? [{ label: "", value: "" }]).map((product) => (
                  <MenuItem value={product.idproducts} key={product.idproducts}>
                    {product.productname}
                  </MenuItem>
                ))}
            </StyledSelect>
          </StyledFormControl>

          {/* Hidden file input */}
          <input
            id="upload-custom-file"
            type="file"
            style={{ display: "none" }}
            onChange={handleUploadNewCustom}
            accept=".zip"
          />

          {/* Upload trigger button or file info display */}
          {selectedProduct && !uploadNewCustom.file[0] && (
            <ColorButton
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleFileUpload}
              disabled={isTestFileUploading}
              sx={{
                height: "44px",
                display: "flex",
                alignItems: "center",
              }}
            >
              Upload ZIP
            </ColorButton>
          )}

          {/* File info and upload actions when file is selected */}
          {selectedProduct && uploadNewCustom.file[0] && (
            <UploadBox>
              <FileInfoBox>
                <CloudUploadIcon sx={{ mr: 1, color: "#FF5336" }} />
                <Typography variant="body1" noWrap>
                  {uploadNewCustom.file[0].name} ({Math.round(uploadNewCustom.file[0].size / 1024)}{" "}
                  KB)
                </Typography>
              </FileInfoBox>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" color="error" onClick={handleClearFile} size="small">
                  Cancel
                </Button>
                <ColorButton
                  variant="contained"
                  onClick={handleUploadCustomFile}
                  disabled={isTestFileUploading}
                  size="small"
                >
                  {isTestFileUploading ? "Uploading..." : "Upload"}
                </ColorButton>
              </Stack>
            </UploadBox>
          )}

          {selectedItems.length > 0 && (
            <ColorButton
              variant="contained"
              startIcon={<DeleteIcon />}
              onClick={deleteProductFileData}
              sx={{ ml: "auto", height: "44px" }}
            >
              Delete Selected
            </ColorButton>
          )}
        </Stack>
      </Box>

      {/* Table Component */}
      {selectedProduct && (
        <Box sx={{ mt: 2 }}>
          {isLoadingNewFiles ? (
            <Typography>Loading data...</Typography>
          ) : newFilesData.length > 0 ? (
            <Table stickyHeader aria-label="custom data table" sx={{ minWidth: 650 }}>
              <TableHead>
                <StyledTableRow>
                  {tableColumnsFromData.map((item) => (
                    <StyledTableCell
                      key={item.id}
                      align={item.align}
                      colSpan={item.colspan}
                      width={item.width}
                      sx={{
                        backgroundColor: "#2550D1",
                        color: "white",
                        borderTopLeftRadius: item === tableColumnsFromData[0] ? 8 : 0,
                        borderTopRightRadius:
                          item === tableColumnsFromData[tableColumnsFromData.length - 1] ? 8 : 0,
                      }}
                    >
                      {item.name}
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {newFilesData.map((item, index) => (
                  <StyledTableRow key={index}>
                    <StyledTableCell width="15%" align="center" colSpan={1}>
                      <Checkbox
                        checked={selectedData.includes(item.FileName)}
                        onChange={(event) => handleCheckboxToggle(event, item)}
                      />
                    </StyledTableCell>
                    <StyledTableCell width="30%" align="center" colSpan={1} sx={{ fontSize: 16 }}>
                      {item.FileName}
                    </StyledTableCell>
                    <StyledTableCell width="30%" align="center" colSpan={2}>
                      {item.LastModified}
                    </StyledTableCell>
                    <StyledTableCell width="20%" align="center" colSpan={1}>
                      {item.FileSize}
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography>No data available</Typography>
          )}
        </Box>
      )}

      {/* Alert Dialog for incorrect file type */}
      <Dialog open={openAlertForZip} onClose={handleCloseAlert}>
        <DialogTitle>Invalid File Format</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please upload only ZIP files. Other file formats are not supported.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAlert} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Alert */}
      <Snackbar
        open={successAlert.open}
        autoHideDuration={6000}
        onClose={handleCloseSuccessAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSuccessAlert}
          severity={successAlert.message.includes("Error") ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {successAlert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NewDataTab;
